import { finalize, take } from 'rxjs/operators';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ContentChild } from '@angular/core';

import { FileService } from '../../services/file.service';
import readableFileSize from '../../utils/readable-file-size';
import { UploaderLoadingDirective } from './directives/uploader-loading.directive';

@Component({
  selector: 'app-uploader',
  templateUrl: './uploader.component.html'
})
export class UploaderComponent implements OnInit {
  @ViewChild('inputFile') inputFile: ElementRef;
  @ContentChild(UploaderLoadingDirective) loadingRenderer: UploaderLoadingDirective;

  @Input() type = 'zone';
  @Input() name = 'file';
  @Input() icon = false;
  @Input() backgroundSize: 'cover' | 'contain' = 'cover';
  @Input() iconSrc = '';
  @Input() autoUpload = true;
  @Input() preview: string = null;
  @Input() label: string = null;
  @Input() minSize = null;
  @Input() maxSize = null;
  @Input() minWidth = null;
  @Input() maxWidth = null;
  @Input() minHeight = null;
  @Input() maxHeight = null;
  @Input() minWidthHeight = null;
  @Input() maxWidthHeight = null;
  @Input() minAspectRatio = null;
  @Input() maxAspectRatio = null;
  @Input() disabled = false;
  @Input() required = false;
  @Input() types = [];
  @Input() service = this.fileService;
  @Input() params: any;
  @Input() readMode = 'readAsDataURL';

  @Output() fileSelected: EventEmitter<any>;
  @Output() fileUploaded: EventEmitter<any>;
  @Output() fileNotUploaded: EventEmitter<any>;

  public uploaded: any;
  public uploading = false;

  constructor(
    private fileService: FileService
  ) {
    this.fileSelected = new EventEmitter();
    this.fileUploaded = new EventEmitter();
  }

  ngOnInit() {}

  click() {
    this.inputFile.nativeElement.click();
  }

  onSelectFile(event1) {
    let reader;
    let file;
    const fileList = event1.target;

    if (fileList.files && fileList.files[0]) {
      file = fileList.files[0];
      reader = new FileReader();
      reader[this.readMode](fileList.files[0]);
      reader.onload = (event2) => {
        file.content = event2.target.result;
        try {
          const image = new Image();

          if (file.type.indexOf('image/') === 0) {
            image.src = event2.target.result;
            image.onload = () => {
              file.width = image.width;
              file.height = image.height;
              this.doValidation(file);
            };
          } else {
            this.doValidation(file);
          }
        } catch (e) {
          this.doValidation(file);
        }
      };
    }
  }

  private doValidation(file, service = null) {
    let isValid = true;
    const data: any = {};
    const errors: any = {};

    if (this.types.length && this.types.indexOf(file.type) === -1) {
      errors.type = {
        actualValue: file.type,
        requiredValue: this.types.join(', ')
      };
    }

    if (this.minSize !== null && file.size < this.minSize) {
      errors.min_size = {
        actualValue: file.size,
        requiredValue: this.minSize,
        readableValue: readableFileSize(this.minSize)
      };
    }

    if (this.maxSize !== null && file.size > this.maxSize) {
      errors.max_size = {
        actualValue: file.size,
        requiredValue: this.maxSize,
        readableValue: readableFileSize(this.maxSize)
      };
    }

    if (file.width) {
      if (this.minWidth !== null && file.width < this.minWidth) {
        errors.min_width = {
          actualValue: file.width,
          requiredValue: this.minWidth
        };
      }

      if (this.maxWidth !== null && file.width > this.maxWidth) {
        errors.max_width = {
          actualValue: file.width,
          requiredValue: this.maxWidth
        };
      }

      if (this.minHeight !== null && file.height < this.minHeight) {
        errors.min_height = {
          actualValue: file.height,
          requiredValue: this.minHeight
        };
      }

      if (this.maxHeight !== null && file.height > this.maxHeight) {
        errors.max_height = {
          actualValue: file.height,
          requiredValue: this.maxHeight
        };
      }

      if (this.minWidthHeight !== null && Math.min(file.width, file.height) < this.minWidthHeight) {
        errors.min_width_height = {
          actualValue: file.height + 'x' + file.height,
          requiredValue: this.minWidthHeight
        };
      }

      if (this.maxWidthHeight !== null && Math.max(file.width, file.height) > this.maxWidthHeight) {
        errors.max_width_height = {
          actualValue: file.height + 'x' + file.height,
          requiredValue: this.maxWidthHeight
        };
      }

      if (this.minAspectRatio !== null && (!file.height || file.width / file.height < this.minAspectRatio)) {
        errors.min_aspect_ratio = {
          actualValue: file.width / file.height,
          requiredValue: this.minAspectRatio * 100
        };
      }

      if (this.maxAspectRatio !== null && (!file.height || file.width / file.height > this.maxAspectRatio)) {
        errors.max_aspect_ratio = {
          actualValue: file.width / file.height,
          requiredValue: this.maxAspectRatio * 100
        };
      }
    }

    this.inputFile.nativeElement.value = '';

    isValid = !!!Object.keys(errors).length;

    data.$valid = isValid;
    data.$invalid = !isValid;
    data.raw = file;
    data.file = {
      'name': file.name,
      'size': file.size,
      'type': file.type,
      'date': file.lastModifiedDate,
      'content': file.content
    };
    data.errors = errors;

    delete data.raw.content;

    this.fileSelected.emit(data);

    if (data.$valid && this.autoUpload) {
      this.upload(data, service);
    }
  }

  upload(file, service = null) {
    this.uploading = true;

    this.service.upload(file.raw, this.params)
      .pipe(
        take(1),
        finalize(() => {
          this.uploading = false;
        }))
      .subscribe(
        (response) => {
          this.fileUploaded.emit(response);
        },
        (error) => {
          this.fileNotUploaded.emit(error);
        }
      );
  }
}
